import { useState } from 'react';
import { LoadingSpinner } from '@/shared/components/Loading';
import { useReferralsLeaderboard, useFlagUser, useOverrideUserTier, useAdjustUserBalance, formatCents } from '@/services/referrals';
import { LeaderboardEntry } from '@/models/referral';

const LeaderboardTab = () => {
    const [limit, setLimit] = useState(25);
    const { data, isLoading } = useReferralsLeaderboard(limit);
    const [actingOn, setActingOn] = useState<{ user: LeaderboardEntry; mode: 'tier' | 'flag' | 'balance' } | null>(null);

    if (isLoading) return <LoadingSpinner />;
    if (data?.status === 'error') return <div className="alert alert-danger">{data.error?.message || 'Failed to load leaderboard.'}</div>;
    const users = data?.data?.users || [];

    return (
        <div>
            <div className="d-flex justify-content-between align-items-center mb-3">
                <div>
                    <strong>{users.length}</strong> top referrers
                </div>
                <select className="form-select" style={{ width: 120 }} value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
                    <option value={25}>Top 25</option>
                    <option value={50}>Top 50</option>
                    <option value={100}>Top 100</option>
                </select>
            </div>
            <div className="table-responsive">
                <table className="table table-hover align-middle">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Member</th>
                            <th>Tier</th>
                            <th className="text-end">Qualified</th>
                            <th className="text-end">Total</th>
                            <th className="text-end">Lifetime</th>
                            <th className="text-end">Pending</th>
                            <th className="text-end">Available</th>
                            <th>Status</th>
                            <th className="text-end">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {users.map((u, i) => (
                            <tr key={u.id}>
                                <td>{i + 1}</td>
                                <td>
                                    <div>{u.name || u.email}</div>
                                    <div className="text-muted small font-monospace">{u.id}</div>
                                </td>
                                <td>Tier {u.tier}</td>
                                <td className="text-end">{u.qualifiedCount}</td>
                                <td className="text-end">{u.totalCount}</td>
                                <td className="text-end">{formatCents(u.lifetimeCents)}</td>
                                <td className="text-end">{formatCents(u.pendingCents)}</td>
                                <td className="text-end">{formatCents(u.availableCents)}</td>
                                <td>
                                    {u.flagged ? <span className="badge text-bg-danger">Flagged</span> : <span className="badge text-bg-success">Active</span>}
                                </td>
                                <td className="text-end">
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary me-1"
                                        onClick={() => setActingOn({ user: u, mode: 'tier' })}
                                    >
                                        Tier
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary me-1"
                                        onClick={() => setActingOn({ user: u, mode: 'flag' })}
                                    >
                                        {u.flagged ? 'Unflag' : 'Flag'}
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-sm btn-outline-secondary"
                                        onClick={() => setActingOn({ user: u, mode: 'balance' })}
                                    >
                                        Balance
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {users.length === 0 && (
                            <tr>
                                <td colSpan={10} className="text-center text-muted py-4">
                                    No referrers yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            {actingOn && actingOn.mode === 'tier' && <TierOverrideModal user={actingOn.user} onClose={() => setActingOn(null)} />}
            {actingOn && actingOn.mode === 'flag' && <FlagModal user={actingOn.user} onClose={() => setActingOn(null)} />}
            {actingOn && actingOn.mode === 'balance' && <BalanceModal user={actingOn.user} onClose={() => setActingOn(null)} />}
        </div>
    );
};

export default LeaderboardTab;

const TierOverrideModal = ({ user, onClose }: { user: LeaderboardEntry; onClose: () => void }) => {
    const [tier, setTier] = useState<1 | 2 | 3 | 'auto'>(user.tier);
    const [reason, setReason] = useState('');
    const mutation = useOverrideUserTier();
    const [error, setError] = useState<string | null>(null);

    const submit = async () => {
        setError(null);
        try {
            // callApi resolves { status:'error' } rather than throwing — inspect it
            // so a failed override doesn't silently close as success.
            const resp = await mutation.mutateAsync({ userId: user.id, tier: tier === 'auto' ? null : tier, reason });
            if (resp.status === 'error') {
                setError(resp.error?.message ?? 'Failed to override tier.');
                return;
            }
            onClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to override tier.');
        }
    };

    return (
        <ModalShell title={`Override tier — ${user.email}`} onClose={onClose}>
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <div className="mb-3">
                <label className="form-label">New tier</label>
                <select
                    className="form-select"
                    value={tier}
                    onChange={(e) => setTier(e.target.value === 'auto' ? 'auto' : (Number(e.target.value) as 1 | 2 | 3))}
                >
                    <option value="auto">Auto (clear override)</option>
                    <option value={1}>Tier 1 · Practitioner (10%)</option>
                    <option value={2}>Tier 2 · Ambassador (20%)</option>
                    <option value={3}>Tier 3 · Circle (30%)</option>
                </select>
            </div>
            <div className="mb-3">
                <label className="form-label">Reason (optional)</label>
                <textarea className="form-control" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
            <div className="d-flex justify-content-end gap-2">
                <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                    Cancel
                </button>
                <button type="button" className="btn btn-primary" disabled={mutation.isPending} onClick={submit}>
                    {mutation.isPending ? 'Saving…' : 'Save'}
                </button>
            </div>
        </ModalShell>
    );
};

const FlagModal = ({ user, onClose }: { user: LeaderboardEntry; onClose: () => void }) => {
    const flagged = !user.flagged;
    const [reason, setReason] = useState('');
    const mutation = useFlagUser();
    const [error, setError] = useState<string | null>(null);
    const submit = async () => {
        setError(null);
        try {
            const resp = await mutation.mutateAsync({ userId: user.id, flagged, reason });
            if (resp.status === 'error') {
                setError(resp.error?.message ?? 'Failed to update flag.');
                return;
            }
            onClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to update flag.');
        }
    };
    return (
        <ModalShell title={`${flagged ? 'Flag' : 'Unflag'} — ${user.email}`} onClose={onClose}>
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <div className="mb-3">
                <label className="form-label">Reason</label>
                <textarea className="form-control" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
            <div className="d-flex justify-content-end gap-2">
                <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                    Cancel
                </button>
                <button type="button" className={flagged ? 'btn btn-danger' : 'btn btn-success'} disabled={mutation.isPending} onClick={submit}>
                    {flagged ? 'Flag user' : 'Unflag user'}
                </button>
            </div>
        </ModalShell>
    );
};

const BalanceModal = ({ user, onClose }: { user: LeaderboardEntry; onClose: () => void }) => {
    const [delta, setDelta] = useState('0');
    const [reason, setReason] = useState('');
    const mutation = useAdjustUserBalance();
    const [error, setError] = useState<string | null>(null);
    const submit = async () => {
        const deltaDollars = parseFloat(delta || '0');
        if (!Number.isFinite(deltaDollars)) return;
        setError(null);
        try {
            // Touches the payouts ledger — a failed adjustment must NOT close as success.
            const resp = await mutation.mutateAsync({ userId: user.id, deltaCents: Math.round(deltaDollars * 100), reason });
            if (resp.status === 'error') {
                setError(resp.error?.message ?? 'Failed to adjust balance.');
                return;
            }
            onClose();
        } catch (e) {
            setError(e instanceof Error ? e.message : 'Failed to adjust balance.');
        }
    };
    return (
        <ModalShell title={`Adjust balance — ${user.email}`} onClose={onClose}>
            {error && <div className="alert alert-danger py-2">{error}</div>}
            <p className="text-muted small">Current available: {formatCents(user.availableCents)}. Use a negative amount to deduct.</p>
            <div className="mb-3">
                <label className="form-label">Delta (USD)</label>
                <input type="number" step="0.01" className="form-control" value={delta} onChange={(e) => setDelta(e.target.value)} />
            </div>
            <div className="mb-3">
                <label className="form-label">Reason (required)</label>
                <textarea className="form-control" rows={3} value={reason} onChange={(e) => setReason(e.target.value)} />
            </div>
            <div className="d-flex justify-content-end gap-2">
                <button type="button" className="btn btn-outline-secondary" onClick={onClose}>
                    Cancel
                </button>
                <button type="button" className="btn btn-primary" disabled={mutation.isPending || reason.trim().length === 0} onClick={submit}>
                    {mutation.isPending ? 'Saving…' : 'Adjust balance'}
                </button>
            </div>
        </ModalShell>
    );
};

const ModalShell = ({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) => (
    <div className="modal show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex={-1} role="dialog" onClick={onClose}>
        <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
                <div className="modal-header">
                    <h5 className="modal-title">{title}</h5>
                    <button type="button" className="btn-close" onClick={onClose} aria-label="Close" />
                </div>
                <div className="modal-body">{children}</div>
            </div>
        </div>
    </div>
);
